import { AppInsightsContext, AppInsightsErrorBoundary } from "@microsoft/applicationinsights-react-js";
import { ApplicationInsights, ITelemetryItem } from "@microsoft/applicationinsights-web";
import { ReactPlugin } from "@microsoft/applicationinsights-react-js";
import { createBrowserHistory } from "history";
import React, { useContext, useEffect } from "react";
import { MsTeamsContext } from "msTeams/store";
import { mapOperationNameToDisplayName } from "./ApplicationInsightsUtils";
import { PowerBiTelemetryExclusion } from "./telemetry/PowerBiTelemetryExclusion";

const browserHistory = createBrowserHistory({ window: window });
const reactPlugin = new ReactPlugin();
const appInsights = new ApplicationInsights({
    config: {
        connectionString: process.env.REACT_APP_APP_INSIGHTS_CONNECTION_STRING || "",
        extensions: [reactPlugin],
        extensionConfig: {
            [reactPlugin.identifier]: { history: browserHistory },
        },
        enableCorsCorrelation: true,
        autoTrackPageVisitTime: true,
        enableAutoRouteTracking: true,
        enableUnhandledPromiseRejectionTracking: true,
    },
});

const useAppIns = process.env.REACT_APP_APP_INSIGHTS_CONNECTION_STRING ? true : false;

if (useAppIns) {
    appInsights.loadAppInsights();
    appInsights.appInsights.addTelemetryInitializer((telemetryItem) => {
        if (isTelemetryItemToBeIgnored(telemetryItem)) {
            return false; // false means do not send telemetry item
        }

        if (telemetryItem.tags) {
            telemetryItem.tags["ai.cloud.role"] = "PinPoint FrontEnd";
        }

        if (telemetryItem?.ext?.trace && telemetryItem?.name && telemetryItem.name.endsWith(".Pageview")) {
            const pageName = mapOperationNameToDisplayName(telemetryItem);

            if (telemetryItem?.baseData) {
                telemetryItem.baseData["name"] = pageName;
            }

            telemetryItem!.ext!.trace!.name = pageName;
            telemetryItem.data = telemetryItem.data ?? {};
            telemetryItem.data["screenWidth"] = window.screen.width;
            telemetryItem.data["screenHeight"] = window.screen.height;
        }
    });
}

const isTelemetryItemToBeIgnored = (telemetryItem: ITelemetryItem) => {
    if (new PowerBiTelemetryExclusion(telemetryItem).isActive()) {
        return true;
    }

    return false;
};

const AppInsights: React.FC = (props) => {
    const msTeamsContext = useContext(MsTeamsContext);

    useEffect(() => {
        if (useAppIns && msTeamsContext.isInitialized && msTeamsContext.userObjectId) {
            appInsights.setAuthenticatedUserContext(msTeamsContext.userObjectId);
        }
    }, []);

    if (!useAppIns) {
        return <>{props.children}</>;
    }

    if (useAppIns && !msTeamsContext) {
        return null;
    }

    return (
        <AppInsightsErrorBoundary onError={() => <h1>Something went wrong</h1>} appInsights={reactPlugin}>
            <AppInsightsContext.Provider value={reactPlugin}>{props.children}</AppInsightsContext.Provider>
        </AppInsightsErrorBoundary>
    );
};

export default AppInsights;
